import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { InstrumentNoteEditor } from '~/components/instrument/instrument-note-editor.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { getInstrumentLayoutPageDataQueryOptions } from '~/server/instrument/instrument-layout-page-data-query.ts';
import { getInstrumentOverviewPageDataQueryOptions } from '~/server/instrument/instrument-overview-page-data-query.ts';
import { cn } from '~/utils/cn.ts';
import { ClientOnly } from '~/utils/is-browser.tsx';
import InstrumentChartV2 from '~/views/instrument/components/chart-components.tsx';
import { InsiderTransactionTable } from '~/views/instrument/components/insider-transactions-table.tsx';
import InstrumentBasicValue from '~/views/instrument/components/instrument-basic-value.tsx';
import InstrumentCalendar from '~/views/instrument/components/instrument-calendar.tsx';
import InstrumentDescription from '~/views/instrument/components/instrument-description.tsx';
import { InstrumentMarketSnapshot } from '~/views/instrument/components/instrument-market-snapshot.tsx';
import { InstrumentOverviewWidget } from '~/views/instrument/components/instrument-overview-widget.tsx';
import { NewsTable } from '~/views/instrument/components/news-and-research-table.tsx';
import {
	getInstrumentOverviewKind,
	getInstrumentOverviewWidgets,
} from '~/views/instrument/utils/instrument-overview-config.ts';

export const Route = createFileRoute('/instrument/$ticker/')({
	component: InstrumentOverviewPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(getInstrumentOverviewPageDataQueryOptions({ ticker: params.ticker })),
	pendingComponent: InstrumentOverviewPagePending,
	pendingMinMs: 250,
});

function InstrumentOverviewPagePending() {
	return (
		<div className="space-y-1.5">
			<div className="grid w-full grid-cols-1 gap-1.5 xl:grid-cols-12">
				<div className="grid grid-cols-1 gap-1.5 xl:col-span-4">
					<Skeleton className="min-h-[180px] w-full rounded-md border border-border bg-card" />
					<Skeleton className="min-h-[180px] w-full rounded-md border border-border bg-card" />
				</div>
				<div className="xl:col-span-8">
					<Skeleton className="min-h-[560px] w-full rounded-md border border-border bg-card" />
				</div>
			</div>
			<div className="grid w-full grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-12">
				<Skeleton className="min-h-[220px] w-full rounded-md border border-border bg-card" />
				<Skeleton className="min-h-[220px] w-full rounded-md border border-border bg-card" />
			</div>
		</div>
	);
}

function ChartLoading() {
	return (
		<div className="m-4 flex flex-col items-center gap-y-2">
			<Skeleton className="h-[100px] w-full rounded-sm bg-muted p-2" />
			<Skeleton className="h-[300px] w-full rounded-sm bg-muted p-2" />
			<Skeleton className="h-[100px] w-full rounded-sm bg-muted p-2" />
		</div>
	);
}

function InstrumentCharts({
	ranges,
	ticker,
	userLocale,
}: {
	readonly ranges: string[] | null | undefined;
	readonly ticker: string;
	readonly userLocale: string;
}) {
	if (!ranges || ranges.length === 0) {
		return <ChartLoading />;
	}

	return (
		<div className="h-full min-h-[560px]">
			<InstrumentChartV2 key={ticker} ranges={ranges} ticker={ticker} userLocale={userLocale} />
		</div>
	);
}

function InstrumentOverviewPage() {
	const t = useTranslations();
	const { ticker: routeTicker } = Route.useParams();
	const { data: commonInstrumentData } = useSuspenseQuery(
		getInstrumentLayoutPageDataQueryOptions({ ticker: routeTicker }),
	);
	const { data } = useSuspenseQuery(getInstrumentOverviewPageDataQueryOptions({ ticker: routeTicker }));
	const {
		companyCalendar,
		insiderTransactions,
		companyNetDebt,
		defaultKeyStatistics,
		marketNews,
		userCompanyNote,
		userLocale,
	} = data;
	const { instrument, ranges } = commonInstrumentData;
	const ticker = instrument?.ticker;
	const instrumentKind = getInstrumentOverviewKind(
		instrument?.type ?? commonInstrumentData?.instrumentFetch?.quoteType,
	);
	const widgets = getInstrumentOverviewWidgets(instrumentKind).map((widget) => ({
		...widget,
		description: t(widget.descriptionId),
		title: t(widget.titleId),
	}));
	const heroWidget = widgets.find((widget) => widget.zone === 'hero');
	const isHeroDefaultOpen = heroWidget?.defaultOpen ?? true;
	const [isHeroWidgetOpen, setIsHeroWidgetOpen] = React.useState(isHeroDefaultOpen);

	React.useEffect(() => {
		setIsHeroWidgetOpen(isHeroDefaultOpen);
	}, [isHeroDefaultOpen]);

	if (!ticker) {
		return null;
	}

	const instrumentTicker: string = ticker;
	const sidebarWidgets = widgets.filter((widget) => widget.zone === 'sidebar');
	const secondaryWidgets = widgets.filter((widget) => widget.zone === 'secondary');
	const fullWidthWidgets = widgets.filter((widget) => widget.zone === 'full');

	function renderWidget(widget: (typeof widgets)[number]) {
		if (widget.id === 'description') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					<InstrumentDescription description={instrument?.company?.description} />
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'basic-value') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					<React.Suspense
						fallback={<Skeleton className={cn('min-h-[120px] w-full rounded-md', 'bg-card', 'border border-border')} />}
					>
						<InstrumentBasicValue
							companyNetDebt={companyNetDebt?.company_balance_sheet?.net_debt}
							companyShareCount={instrument?.company?.company_share_count}
							defaultKeyStatistics={defaultKeyStatistics}
							ticker={instrumentTicker}
							userLocale={userLocale}
						/>
					</React.Suspense>
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'market-snapshot') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					<InstrumentMarketSnapshot
						displayData={commonInstrumentData?.instrumentFetch}
						instrumentCurrency={instrument?.currency}
						instrumentType={instrument?.type}
						ticker={ticker}
						userLocale={userLocale}
					/>
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'calendar' && companyCalendar) {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					<InstrumentCalendar calendar={companyCalendar} userLocale={userLocale} />
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'chart') {
			return (
				<InstrumentOverviewWidget
					className={cn(widget.layoutClassName, !isHeroWidgetOpen ? 'xl:col-span-6' : undefined)}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					isOpen={isHeroWidgetOpen}
					key={widget.id}
					onOpenChange={setIsHeroWidgetOpen}
					title={widget.title}
				>
					<div className={widget.previewClassName}>
						<InstrumentCharts ranges={ranges} ticker={instrumentTicker} userLocale={userLocale} />
					</div>
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'news') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					{marketNews.length > 0 ? (
						<div className={cn('overflow-hidden rounded-md border border-border/70', widget.previewClassName)}>
							<NewsTable news={marketNews} userLocale={userLocale} />
						</div>
					) : (
						<div className="rounded-md border border-border/70 border-dashed bg-muted/10 px-4 py-8 text-muted-foreground text-sm">
							{t('instrument.noNews')}
						</div>
					)}
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'insider') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					{insiderTransactions && insiderTransactions.length > 0 ? (
						<div className={widget.previewClassName}>
							<InsiderTransactionTable insiderTransactions={insiderTransactions} userLocale={userLocale} />
						</div>
					) : (
						<div className="rounded-md border border-border/70 border-dashed bg-muted/10 px-4 py-8 text-muted-foreground text-sm">
							{t('instrument.noInsiderTransactions')}
						</div>
					)}
				</InstrumentOverviewWidget>
			);
		}

		if (widget.id === 'notes') {
			return (
				<InstrumentOverviewWidget
					className={widget.layoutClassName}
					contentClassName={widget.contentClassName}
					defaultOpen={widget.defaultOpen}
					description={widget.description}
					headerClassName={widget.headerClassName}
					key={widget.id}
					title={widget.title}
				>
					<div className={widget.previewClassName}>
						<ClientOnly
							fallback={
								<Skeleton className={cn('min-h-[260px] w-full rounded-md', 'bg-card', 'border border-border')} />
							}
						>
							{() => <InstrumentNoteEditor userCompanyNote={userCompanyNote} userLocale={userLocale} />}
						</ClientOnly>
					</div>
				</InstrumentOverviewWidget>
			);
		}

		return null;
	}

	return (
		<div className="space-y-1.5">
			<div className="grid w-full grid-cols-1 gap-1.5 xl:grid-cols-12">
				<div className={cn('grid grid-cols-1 gap-1.5', !isHeroWidgetOpen ? 'xl:col-span-6' : 'xl:col-span-4')}>
					{sidebarWidgets.map(renderWidget)}
				</div>
				{heroWidget ? (
					<div className={cn(!isHeroWidgetOpen ? 'xl:col-span-6' : 'xl:col-span-8')}>{renderWidget(heroWidget)}</div>
				) : null}
			</div>
			{secondaryWidgets.length > 0 ? (
				<div className="grid w-full grid-cols-1 gap-1.5 md:grid-cols-2 xl:grid-cols-12">
					{secondaryWidgets.map(renderWidget)}
				</div>
			) : null}
			{fullWidthWidgets.length > 0 ? <div className="space-y-1.5">{fullWidthWidgets.map(renderWidget)}</div> : null}
		</div>
	);
}
